import { useEffect, useRef, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, Upload, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PrescriptionPreview from "@/components/PrescriptionPreview";
import CallOutVoicePicker from "@/components/CallOutVoicePicker";
import BuildInfo from "@/components/BuildInfo";

const EMPTY_BLOCK = { name: "", lines: [""] };

const SAMPLE = {
  prescription_number: "RX-PREVIEW",
  patient_snapshot: { full_name: "Sample Patient", address: "123 Main St., Quezon City", age: 35, gender: "Male" },
  date: new Date().toISOString(),
  medicines: [
    { medicine_name: "Amoxicillin 500mg", dosage: "1 capsule", frequency: "Three times daily", duration: "7 days", instruction: "After meals" },
  ],
};

export default function Profile() {
  const [p, setP] = useState(null);
  const [rxProfile, setRxProfile] = useState(null);
  const [settings, setSettings] = useState({});
  const [busy, setBusy] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwBusy, setPwBusy] = useState(false);

  // Live-preview scaling: fit the paper to the preview column width so it fills
  // the space and stays readable, whatever the paper size.
  const previewBoxRef = useRef(null);
  const [previewScale, setPreviewScale] = useState(0.7);
  const _PW = { A4: 210, Letter: 216, Half: 148 };
  const _PH = { A4: 297, Letter: 279, Half: 210 };
  const paperWmm = (settings?.paper_size === "Custom" ? Number(settings?.custom_width) : _PW[settings?.paper_size]) || 210;
  const paperHmm = (settings?.paper_size === "Custom" ? Number(settings?.custom_height) : _PH[settings?.paper_size]) || 297;
  const PX_PER_MM = 96 / 25.4;

  useEffect(() => {
    const el = previewBoxRef.current;
    if (!el) return;
    const compute = () => {
      const avail = el.clientWidth - 32; // minus the p-4 padding
      if (avail <= 0) return;
      setPreviewScale(Math.max(0.4, Math.min(avail / (paperWmm * PX_PER_MM), 1.5)));
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [paperWmm, PX_PER_MM]);
  const changePassword = async () => {
    if (!pwForm.current || !pwForm.next) { toast.error("Fill in current and new password"); return; }
    if (pwForm.next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error("New passwords do not match"); return; }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password changed");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setPwBusy(false);
    }
  };
  const sigRef = useRef(null);
  const wmRef = useRef(null);

  const load = async () => {
    const [me, rx, st] = await Promise.all([
      api.get("/profile"),
      api.get("/doctor-rx-profile/me"),
      api.get("/print-settings"),
    ]);
    const next = { ...me.data };
    if (!Array.isArray(next.affiliations) || next.affiliations.length === 0) next.affiliations = [EMPTY_BLOCK];
    setP(next);
    setRxProfile(rx.data || {});
    setSettings(st.data || {});
  };
  useEffect(() => { load(); }, []);

  const update = (patch) => setP((prev) => ({ ...prev, ...patch }));
  const updateRx = (patch) => setRxProfile((prev) => ({ ...(prev || {}), ...patch }));

  const layoutTemplate = rxProfile?.layout_template || "classic";
  const clinics = Array.isArray(rxProfile?.clinics) ? rxProfile.clinics : [];
  const setClinics = (next) => updateRx({ clinics: next });
  const addClinic = () => setClinics([...clinics, { name: "", schedule: "", contact: "" }]);
  const removeClinic = (i) => setClinics(clinics.filter((_, idx) => idx !== i));
  const setClinic = (i, patch) => { const n = [...clinics]; n[i] = { ...n[i], ...patch }; setClinics(n); };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: p.name,
        doctor_name: p.doctor_name,
        specialty: p.specialty,
        license_number: p.license_number,
        ptr_number: p.ptr_number,
        affiliations: (p.affiliations || []).filter((b) => (b.name || "").trim() || (b.lines || []).some((l) => l.trim())),
      };
      await api.put("/profile", payload);

      // Also save the doctor RX profile (canonical settings that follow doctor across hospitals)
      const rxPayload = {
        doctor_name: p.doctor_name,
        specialty: p.specialty,
        license_number: p.license_number,
        ptr_number: p.ptr_number,
        s2_number: rxProfile?.s2_number || "",
        receipt_business_name: rxProfile?.receipt_business_name || "",
        signature_image_url: p.signature_image_url || "",
        affiliations: payload.affiliations,
        medicine_font_size: Number(rxProfile?.medicine_font_size) || 12,
        instruction_font_size: Number(rxProfile?.instruction_font_size) || 10,
        rows_per_page: Number(rxProfile?.rows_per_page) || 5,
        row_height: Number(rxProfile?.row_height) || 11,
        layout_template: layoutTemplate,
        clinics: clinics.filter((c) => (c.name || "").trim() || (c.schedule || "").trim() || (c.contact || "").trim()),
        affiliations_footer: rxProfile?.affiliations_footer || "",
        security_code_enabled: rxProfile?.security_code_enabled !== false,
        follow_up_enabled: rxProfile?.follow_up_enabled !== false,
        // Per-doctor watermark (follows the doctor to every affiliated hospital)
        use_own_watermark: !!rxProfile?.use_own_watermark,
        show_watermark: rxProfile?.show_watermark !== false,
        watermark_type: rxProfile?.watermark_type || "text",
        watermark_text: rxProfile?.watermark_text || "Rx",
        watermark_image_url: rxProfile?.watermark_image_url || "",
        watermark_opacity: Number(rxProfile?.watermark_opacity ?? 0.08),
        watermark_image_size: Number(rxProfile?.watermark_image_size ?? 55),
        watermark_rotation: Number(rxProfile?.watermark_rotation ?? -25),
        watermark_font_size: Number(rxProfile?.watermark_font_size ?? 72),
      };
      await api.put("/doctor-rx-profile/me", rxPayload);
      toast.success("Profile saved");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const uploadSig = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      // Upload to doctor RX profile (canonical) — also mirrored to user record by backend
      const { data } = await api.post("/doctor-rx-profile/me/signature-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      update({ signature_image_url: data.signature_image_url });
      toast.success("Signature uploaded");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
    finally { if (e.target) e.target.value = ""; }
  };

  const uploadWatermark = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/doctor-rx-profile/me/watermark-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      updateRx({ watermark_image_url: data.watermark_image_url, watermark_type: "image", use_own_watermark: true });
      toast.success("Watermark uploaded");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail)); }
    finally { if (e.target) e.target.value = ""; }
  };

  const addBlock = () => update({ affiliations: [...(p.affiliations || []), { ...EMPTY_BLOCK }] });
  const removeBlock = (i) => update({ affiliations: (p.affiliations || []).filter((_, idx) => idx !== i) });
  const setBlock = (i, patch) => {
    const next = [...(p.affiliations || [])];
    next[i] = { ...next[i], ...patch };
    update({ affiliations: next });
  };
  const addLine = (i) => setBlock(i, { lines: [...(p.affiliations[i].lines || []), ""] });
  const removeLine = (i, li) => setBlock(i, { lines: (p.affiliations[i].lines || []).filter((_, idx) => idx !== li) });
  const setLine = (i, li, v) => {
    const next = [...(p.affiliations[i].lines || [])];
    next[li] = v;
    setBlock(i, { lines: next });
  };

  if (!p) return <div className="p-8 text-slate-500">Loading...</div>;

  return (
    <div className="p-6 md:p-8 max-w-[1600px]" data-testid="profile-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>My Doctor Profile</h1>
          <p className="text-slate-500 text-sm mt-1">Header text, license, affiliations, and signature for your prescriptions. Each doctor has their own.</p>
        </div>
        <Button onClick={save} className="bg-blue-600 hover:bg-blue-700" disabled={busy} data-testid="btn-save-profile">
          <Save size={14} className="mr-2" /> Save Profile
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="space-y-4">
          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Doctor Identity</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Display Name (header line 1)</Label><Input value={p.doctor_name || ""} onChange={(e) => update({ doctor_name: e.target.value })} data-testid="profile-doctor-name" /></div>
              <div className="col-span-2"><Label>Specialty</Label><Input value={p.specialty || ""} onChange={(e) => update({ specialty: e.target.value })} placeholder="Internal Medicine - Adult Diseases" data-testid="profile-specialty" /></div>
              <div className="col-span-2">
                <Label>Receipt business name (optional)</Label>
                <Input value={rxProfile?.receipt_business_name || ""} onChange={(e) => updateRx({ receipt_business_name: e.target.value })} placeholder="e.g. Gonzales Medical Clinic" data-testid="profile-receipt-business" />
                <p className="text-[11px] text-slate-500 mt-1">Printed as the business header on POS receipts for YOUR transactions. Leave blank to use the clinic’s receipt header.</p>
              </div>
              <div><Label>License #</Label><Input value={p.license_number || ""} onChange={(e) => update({ license_number: e.target.value })} data-testid="profile-license" /></div>
              <div><Label>PTR #</Label><Input value={p.ptr_number || ""} onChange={(e) => update({ ptr_number: e.target.value })} data-testid="profile-ptr" /></div>
              <div className="col-span-2"><Label>S2 #</Label><Input value={rxProfile?.s2_number || ""} onChange={(e) => updateRx({ s2_number: e.target.value })} data-testid="profile-s2" placeholder="DEA / S2 license number"/></div>
              <div className="col-span-2"><Label className="text-xs text-slate-500">Account login</Label><div className="text-sm text-slate-700">{p.email} <span className="text-xs text-slate-400">({p.role})</span></div></div>
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Prescription Layout</h2>
            <p className="text-xs text-slate-500 mb-3">Choose how your prescription is arranged. Your header, signature, and watermark follow you to every affiliated hospital.</p>
            <div className="mb-4">
              <Label>Layout template</Label>
              <select
                value={layoutTemplate}
                onChange={(e) => updateRx({ layout_template: e.target.value })}
                className="mt-1 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
                data-testid="profile-layout-template"
              >
                <option value="classic">Classic — centered name, 3-column affiliations</option>
                <option value="multi_clinic">Multi-clinic — name left, clinic list right</option>
              </select>
            </div>

            {layoutTemplate === "multi_clinic" ? (
              <div className="space-y-3" data-testid="multiclinic-config">
                <div className="flex items-center justify-between">
                  <Label className="mb-0">Clinics (right-column rows)</Label>
                  <Button size="sm" variant="outline" onClick={addClinic} data-testid="btn-add-clinic">
                    <Plus size={14} className="mr-1" /> Add clinic
                  </Button>
                </div>
                {clinics.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">No clinics yet — add one row per clinic (name, schedule, contact number).</p>
                ) : null}
                {clinics.map((c, i) => (
                  <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50 space-y-2" data-testid={`clinic-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <Input value={c.name || ""} onChange={(e) => setClinic(i, { name: e.target.value })} placeholder="Clinic / hospital name" className="font-medium" data-testid={`clinic-name-${i}`} />
                      <Button size="sm" variant="ghost" onClick={() => removeClinic(i)} className="text-red-600" data-testid={`clinic-remove-${i}`}>
                        <Trash2 size={14} />
                      </Button>
                    </div>
                    <Input value={c.schedule || ""} onChange={(e) => setClinic(i, { schedule: e.target.value })} placeholder="Room 125A Tue & Fri 10am-12pm; Wed 12pm (per appt)" className="h-8 text-sm" data-testid={`clinic-schedule-${i}`} />
                    <Input value={c.contact || ""} onChange={(e) => setClinic(i, { contact: e.target.value })} placeholder="Contact number (e.g. 0917-124-0301)" className="h-8 text-sm" data-testid={`clinic-contact-${i}`} />
                  </div>
                ))}

                <div>
                  <Label>Hospital affiliations footer (optional)</Label>
                  <textarea
                    value={rxProfile?.affiliations_footer || ""}
                    onChange={(e) => updateRx({ affiliations_footer: e.target.value })}
                    placeholder="ACE Medical Center Pangasinan • Central Pangasinan Hospital • Luzon Medical Center • ..."
                    className="mt-1 w-full min-h-[64px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
                    data-testid="profile-affiliations-footer"
                  />
                </div>

                <div className="flex flex-wrap gap-4 pt-1">
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={rxProfile?.follow_up_enabled !== false} onChange={(e) => updateRx({ follow_up_enabled: e.target.checked })} data-testid="profile-followup-toggle" />
                    Show &quot;Follow Up&quot; line
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-700">
                    <input type="checkbox" checked={rxProfile?.security_code_enabled !== false} onChange={(e) => updateRx({ security_code_enabled: e.target.checked })} data-testid="profile-security-toggle" />
                    Show security code
                  </label>
                </div>
              </div>
            ) : null}
          </Card>

          <Card className="p-5 border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900" style={{ fontFamily: "Work Sans" }}>Affiliations (3-column header grid)</h2>
              <Button size="sm" variant="outline" onClick={addBlock} data-testid="btn-add-affiliation">
                <Plus size={14} className="mr-1" /> Add column
              </Button>
            </div>
            <p className="text-xs text-slate-500 mb-3">Each block becomes a column of your printed letterhead. The first block&apos;s &quot;Affiliations:&quot; label is auto-added on the Rx.</p>
            <div className="space-y-4">
              {(p.affiliations || []).map((block, i) => (
                <div key={i} className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid={`affil-block-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Input
                      value={block.name || ""}
                      onChange={(e) => setBlock(i, { name: e.target.value })}
                      placeholder="HOSPITAL / CLINIC NAME"
                      className="font-bold uppercase"
                      data-testid={`affil-name-${i}`}
                    />
                    <Button size="sm" variant="ghost" onClick={() => removeBlock(i)} className="text-red-600" data-testid={`affil-remove-${i}`}>
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="space-y-1">
                    {(block.lines || []).map((line, li) => (
                      <div key={li} className="flex items-center gap-2">
                        <Input
                          value={line}
                          onChange={(e) => setLine(i, li, e.target.value)}
                          placeholder="Address line, schedule, contact, etc."
                          className="h-8 text-sm"
                          data-testid={`affil-line-${i}-${li}`}
                        />
                        <Button size="sm" variant="ghost" onClick={() => removeLine(i, li)} className="text-slate-500 h-7 w-7 p-0">
                          <Trash2 size={12} />
                        </Button>
                      </div>
                    ))}
                    <Button size="sm" variant="link" onClick={() => addLine(i)} className="text-blue-600 h-6 px-0" data-testid={`affil-add-line-${i}`}>
                      <Plus size={12} className="mr-1" /> Add line
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>RX Print Settings</h2>
            <p className="text-xs text-slate-500 mb-3">These follow you across <b>all</b> your hospitals. The hospital&apos;s letterhead/template still controls the page; these control your medicine table format.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Medicine name font size (pt)</Label>
                <Input type="number" step="0.5" min="8" max="20"
                  value={rxProfile?.medicine_font_size ?? 12}
                  onChange={(e) => updateRx({ medicine_font_size: e.target.value })}
                  data-testid="profile-medicine-font" />
              </div>
              <div>
                <Label>Instruction font size (pt)</Label>
                <Input type="number" step="0.5" min="7" max="18"
                  value={rxProfile?.instruction_font_size ?? 10}
                  onChange={(e) => updateRx({ instruction_font_size: e.target.value })}
                  data-testid="profile-instruction-font" />
              </div>
              <div>
                <Label>Rows per page</Label>
                <Input type="number" min="3" max="30"
                  value={rxProfile?.rows_per_page ?? 5}
                  onChange={(e) => updateRx({ rows_per_page: e.target.value })}
                  data-testid="profile-rows-per-page" />
              </div>
              <div>
                <Label>Row height (mm)</Label>
                <Input type="number" step="0.5" min="6" max="20"
                  value={rxProfile?.row_height ?? 11}
                  onChange={(e) => updateRx({ row_height: e.target.value })}
                  data-testid="profile-row-height" />
              </div>
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Signature</h2>
            <div className="max-w-sm">
              <Label>Doctor Signature</Label>
              <div className="mt-1 border border-dashed border-slate-300 rounded-md p-3 text-center bg-slate-50">
                {p.signature_image_url ? (
                  <img src={p.signature_image_url} alt="signature" className="max-h-24 mx-auto mb-2" data-testid="profile-signature-preview" />
                ) : (
                  <div className="text-xs text-slate-500 py-3">No signature uploaded</div>
                )}
                <Button size="sm" variant="outline" onClick={() => sigRef.current?.click()} data-testid="btn-upload-my-signature">
                  <Upload size={14} className="mr-1" /> Upload
                </Button>
                <input ref={sigRef} type="file" accept="image/*" className="hidden" onChange={uploadSig} />
                {p.signature_image_url ? (
                  <button onClick={() => update({ signature_image_url: "" })} className="block mx-auto mt-2 text-xs text-red-600 hover:underline" data-testid="btn-remove-my-signature">Remove</button>
                ) : null}
              </div>
            </div>
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Work Sans" }}>My Watermark</h2>
            <p className="text-xs text-slate-500 mb-3">Prints behind every prescription you write — the same at every hospital you're affiliated with.</p>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-blue-700" checked={!!rxProfile?.use_own_watermark}
                onChange={(e) => updateRx({ use_own_watermark: e.target.checked })} data-testid="wm-use-own" />
              <span className="font-semibold text-slate-800">Use my own watermark (override the clinic's)</span>
            </label>

            {rxProfile?.use_own_watermark ? (
              <div className="mt-4 space-y-4 max-w-lg">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" className="h-4 w-4 accent-blue-700" checked={rxProfile?.show_watermark !== false}
                    onChange={(e) => updateRx({ show_watermark: e.target.checked })} />
                  <span>Show watermark</span>
                </label>

                <div>
                  <Label>Type</Label>
                  <select value={rxProfile?.watermark_type || "text"} onChange={(e) => updateRx({ watermark_type: e.target.value })}
                    className="mt-1 w-full h-10 border border-slate-300 rounded-lg px-3" data-testid="wm-type">
                    <option value="text">Text</option>
                    <option value="image">Image / Logo</option>
                  </select>
                </div>

                {(rxProfile?.watermark_type || "text") === "text" ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <Label>Watermark text</Label>
                      <Input value={rxProfile?.watermark_text || ""} onChange={(e) => updateRx({ watermark_text: e.target.value })} placeholder="e.g. Rx or your name" data-testid="wm-text" />
                    </div>
                    <div>
                      <Label>Font size (pt)</Label>
                      <Input type="number" value={rxProfile?.watermark_font_size ?? 72} onChange={(e) => updateRx({ watermark_font_size: Number(e.target.value) })} />
                    </div>
                    <div>
                      <Label>Rotation (°)</Label>
                      <Input type="number" value={rxProfile?.watermark_rotation ?? -25} onChange={(e) => updateRx({ watermark_rotation: Number(e.target.value) })} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>Watermark image / logo</Label>
                    <div className="mt-1 border border-dashed border-slate-300 rounded-md p-3 text-center bg-slate-50">
                      {rxProfile?.watermark_image_url ? (
                        <img src={rxProfile.watermark_image_url} alt="watermark" className="max-h-24 mx-auto mb-2" data-testid="wm-preview" />
                      ) : (
                        <div className="text-xs text-slate-500 py-3">No watermark image</div>
                      )}
                      <Button size="sm" variant="outline" onClick={() => wmRef.current?.click()} data-testid="btn-upload-watermark">
                        <Upload size={14} className="mr-1" /> Upload
                      </Button>
                      <input ref={wmRef} type="file" accept="image/*" className="hidden" onChange={uploadWatermark} />
                      {rxProfile?.watermark_image_url ? (
                        <button onClick={() => updateRx({ watermark_image_url: "" })} className="block mx-auto mt-2 text-xs text-red-600 hover:underline">Remove</button>
                      ) : null}
                    </div>
                    <div className="mt-2">
                      <Label>Image size (% of page width)</Label>
                      <Input type="number" value={rxProfile?.watermark_image_size ?? 55} onChange={(e) => updateRx({ watermark_image_size: Number(e.target.value) })} />
                    </div>
                  </div>
                )}

                <div>
                  <Label>Opacity (0–1, e.g. 0.08)</Label>
                  <Input type="number" step="0.01" min="0" max="1" value={rxProfile?.watermark_opacity ?? 0.08} onChange={(e) => updateRx({ watermark_opacity: Number(e.target.value) })} data-testid="wm-opacity" />
                </div>
              </div>
            ) : null}
          </Card>

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Change Password</h2>
            <div className="space-y-3 max-w-sm">
              <div><Label>Current password</Label><Input type="password" value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} data-testid="pw-current" /></div>
              <div><Label>New password</Label><Input type="password" value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} placeholder="At least 6 characters" data-testid="pw-new" /></div>
              <div><Label>Confirm new password</Label><Input type="password" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" /></div>
              <Button onClick={changePassword} disabled={pwBusy} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-change-password">
                {pwBusy ? "Saving..." : "Change Password"}
              </Button>
            </div>
          </Card>

          <CallOutVoicePicker />

          <Card className="p-5 border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-2" style={{ fontFamily: "Work Sans" }}>App Version</h2>
            <BuildInfo />
          </Card>
        </div>

        <div>
          <h2 className="font-semibold text-slate-900 mb-3" style={{ fontFamily: "Work Sans" }}>Live Preview</h2>
          <div ref={previewBoxRef} className="bg-slate-100 rounded-md p-4 overflow-auto max-h-[80vh]">
            <div style={{ width: `${paperWmm * PX_PER_MM * previewScale}px`, height: `${paperHmm * PX_PER_MM * previewScale}px`, margin: "0 auto" }}>
            <div style={{ transform: `scale(${previewScale})`, transformOrigin: "top left", width: "fit-content" }}>
              <PrescriptionPreview
                prescription={{
                  ...SAMPLE,
                  doctor_snapshot: {
                    name: p.doctor_name,
                    specialty: p.specialty,
                    license_number: p.license_number,
                    ptr_number: p.ptr_number,
                    s2_number: rxProfile?.s2_number || "",
                    signature_image_url: p.signature_image_url,
                    affiliations: p.affiliations,
                    medicine_font_size: Number(rxProfile?.medicine_font_size) || 12,
                    instruction_font_size: Number(rxProfile?.instruction_font_size) || 10,
                    rows_per_page: Number(rxProfile?.rows_per_page) || 5,
                    row_height: Number(rxProfile?.row_height) || 11,
                    layout_template: layoutTemplate,
                    clinics: clinics,
                    affiliations_footer: rxProfile?.affiliations_footer || "",
                    security_code_enabled: rxProfile?.security_code_enabled !== false,
                    follow_up_enabled: rxProfile?.follow_up_enabled !== false,
                    use_own_watermark: !!rxProfile?.use_own_watermark,
                    show_watermark: rxProfile?.show_watermark !== false,
                    watermark_type: rxProfile?.watermark_type || "text",
                    watermark_text: rxProfile?.watermark_text || "Rx",
                    watermark_image_url: rxProfile?.watermark_image_url || "",
                    watermark_opacity: Number(rxProfile?.watermark_opacity ?? 0.08),
                    watermark_image_size: Number(rxProfile?.watermark_image_size ?? 55),
                    watermark_rotation: Number(rxProfile?.watermark_rotation ?? -25),
                    watermark_font_size: Number(rxProfile?.watermark_font_size ?? 72),
                  },
                }}
                settings={settings}
              />
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
